// TOREFACTO
// - Create generic component to display log
// - Try to reduce size of the switch (concat edge cases, ...)
import { KdsText } from "@kazaar-platform/design-system";
import { RiCloseCircleFill } from "@remixicon/react";
import { T, useTranslate } from "@tolgee/react";
import dayjs from "dayjs";
import {
  type OrderItemDetailsOrderLogReadDto,
  OrderItemDetailsOrderLogReadDtoActionTypeEnum,
} from "@/config/apiClient";
import {
  type TransitionData,
  type TransitionDataFile,
} from "@/types/transitionData";
import { OrderItemPageActivityLogAssignmentProduct } from "./_partials/OrderItemPageActivityLogAssignmentProduct";
import { OrderItemPageActivityLogAssignmentUser } from "./_partials/OrderItemPageActivityLogAssignmentUser";
import { OrderItemPageActivityLogGraphicFile } from "./_partials/OrderItemPageActivityLogGraphicFile";
import { OrderItemPageActivityOrderNumber } from "./_partials/OrderItemPageActivityLogOrderNumber";
import { OrderItemPageActivityLogPreMediaProposition } from "./_partials/OrderItemPageActivityLogPreMediaProposition";
import { OrderItemPageActivityLogProof } from "./_partials/OrderItemPageActivityLogProof";
import { OrderItemPageActivityLogQuote } from "./_partials/OrderItemPageActivityLogQuote";
import { OrderItemPageActivityLogSimple } from "./_partials/OrderItemPageActivityLogSimple";
import { OrderItemPageActivityLogSimpleWithIcon } from "./_partials/OrderItemPageActivityLogSimpleWithIcon";
import { OrderItemPageActivityLogWithFiles } from "./_partials/OrderItemPageActivityLogWithFiles";
import { OrderItemPageActivityLogMessageActionTypeEnum } from "./OrderItemPageActivityLogMessage.constants";

type OrderItemPageActivityLogMessageProps = {
  orderItemLog: OrderItemDetailsOrderLogReadDto;
  associatedOrderItemsLength?: number;
  file?: TransitionDataFile;
};

export const OrderItemPageActivityLogMessage = ({
  orderItemLog,
  associatedOrderItemsLength,
  file,
}: OrderItemPageActivityLogMessageProps) => {
  const { t } = useTranslate();
  const transitionData = orderItemLog.transitionData as TransitionData;

  switch (orderItemLog.actionType) {
    // Add Order Number
    case OrderItemDetailsOrderLogReadDtoActionTypeEnum.AddOrderNumber:
      if (!transitionData?.files?.length) {
        return (
          <OrderItemPageActivityOrderNumber
            orderNumberId={transitionData?.text}
          />
        );
      } else {
        return transitionData?.files?.map((file) => (
          <OrderItemPageActivityOrderNumber
            key={file.url ?? file.blobId}
            file={file}
            orderNumberId={transitionData?.text}
          />
        ));
      }

    // Dispatch Files
    case OrderItemDetailsOrderLogReadDtoActionTypeEnum.AddDispatchFile:
      if (!transitionData?.files?.length) {
        return (
          <OrderItemPageActivityLogSimple
            content={t(
              "order.myOrdersItemDetails.activityFeed.action.dispatchFiles.full"
            )}
          />
        );
      }

      return transitionData?.files?.map((file) => (
        <OrderItemPageActivityLogWithFiles
          key={file.url ?? file.blobId}
          intro={t(
            "order.myOrdersItemDetails.activityFeed.action.dispatchFiles.intro"
          )}
          suffix={t(
            "order.myOrdersItemDetails.activityFeed.action.dispatchFiles.suffix"
          )}
          file={file}
        />
      ));

    // Extra Attachments
    case OrderItemDetailsOrderLogReadDtoActionTypeEnum.AddExtraAttachment:
      return (
        <OrderItemPageActivityLogWithFiles
          intro={t(
            "order.myOrdersItemDetails.activityFeed.action.extraAttachments.intro"
          )}
          suffix={t(
            "order.myOrdersItemDetails.activityFeed.action.extraAttachments.suffix"
          )}
          file={file}
        />
      );

    // Command Placed
    case OrderItemDetailsOrderLogReadDtoActionTypeEnum.Placed:
      return (
        <OrderItemPageActivityLogSimple
          content={t(
            "order.myOrdersItemDetails.activityFeed.action.commandPlaced"
          )}
        />
      );

    // Assignment User
    case OrderItemDetailsOrderLogReadDtoActionTypeEnum.AssignUser:
    case OrderItemDetailsOrderLogReadDtoActionTypeEnum.UnassignUser:
      return (
        <OrderItemPageActivityLogAssignmentUser
          user={transitionData?.relatedUser}
          isUserAssigned={
            OrderItemDetailsOrderLogReadDtoActionTypeEnum.AssignUser ===
            orderItemLog.actionType
          }
        />
      );

    // Assignment Product
    case OrderItemDetailsOrderLogReadDtoActionTypeEnum.AssignProductToOrder:
      return (
        <OrderItemPageActivityLogAssignmentProduct
          assignedProductName={transitionData?.relatedProduct || ""}
        />
      );

    case OrderItemDetailsOrderLogReadDtoActionTypeEnum.ItemSentToStudy:
      return (
        <OrderItemPageActivityLogSimple
          content={t(
            "order.myOrdersItemDetails.activityFeed.action.itemSentToStudy",
            { productName: transitionData?.relatedProduct }
          )}
        />
      );

    // Command Cancelled
    case OrderItemDetailsOrderLogReadDtoActionTypeEnum.Cancelled:
      return (
        <OrderItemPageActivityLogSimpleWithIcon
          title={t("order.myOrdersItemDetails.activityFeed.action.cancelled")}
          iconLabel="order.myOrdersItemDetails.activityFeed.action.infoIcon.cancelled"
          iconState="error"
        />
      );

    // Modification Request
    case OrderItemDetailsOrderLogReadDtoActionTypeEnum.CreatedModificationRequest:
      return (
        <OrderItemPageActivityLogSimple
          content={t(
            "order.myOrdersItemDetails.activityFeed.action.createdModificationRequest"
          )}
        />
      );
    case OrderItemDetailsOrderLogReadDtoActionTypeEnum.RefusedModificationRequest:
      return (
        <OrderItemPageActivityLogSimpleWithIcon
          title={t(
            "order.myOrdersItemDetails.activityFeed.action.refusedModificationRequest"
          )}
          iconLabel="order.myOrdersItemDetails.activityFeed.action.infoIcon.refused"
          iconState="error"
        />
      );
    case OrderItemDetailsOrderLogReadDtoActionTypeEnum.AcceptedModificationRequest:
      return (
        <OrderItemPageActivityLogSimpleWithIcon
          title={t(
            "order.myOrdersItemDetails.activityFeed.action.refusedModificationRequest"
          )}
          iconLabel="order.myOrdersItemDetails.activityFeed.action.infoIcon.validated"
          iconState="success"
        />
      );
    case OrderItemDetailsOrderLogReadDtoActionTypeEnum.CancelledModificationRequest:
      return (
        <OrderItemPageActivityLogSimple
          content={t(
            "order.myOrdersItemDetails.activityFeed.action.cancelledModificationRequest"
          )}
        />
      );

    // Retroplanning
    case OrderItemDetailsOrderLogReadDtoActionTypeEnum.RetroplanningShared:
      return (
        <OrderItemPageActivityLogSimple
          content={t(
            "order.myOrdersItemDetails.activityFeed.action.retroplanningShared"
          )}
        />
      );

    // Graphic Files
    case OrderItemDetailsOrderLogReadDtoActionTypeEnum.AddGraphic:
    case OrderItemDetailsOrderLogReadDtoActionTypeEnum.DeleteGraphic:
      if (!transitionData?.files?.length) {
        return (
          <OrderItemPageActivityLogGraphicFile
            deleted={
              orderItemLog.actionType ===
              OrderItemDetailsOrderLogReadDtoActionTypeEnum.DeleteGraphic
            }
          />
        );
      }

      return transitionData?.files?.map((file) => (
        <OrderItemPageActivityLogGraphicFile
          key={file.url ?? file.blobId}
          file={file}
          deleted={
            orderItemLog.actionType ===
            OrderItemDetailsOrderLogReadDtoActionTypeEnum.DeleteGraphic
          }
        />
      ));

    // Quotes
    case OrderItemDetailsOrderLogReadDtoActionTypeEnum.QuoteCancelledPreviouslyRefused:
    case OrderItemDetailsOrderLogReadDtoActionTypeEnum.QuoteCancelledPreviouslyValidated:
    case OrderItemDetailsOrderLogReadDtoActionTypeEnum.QuoteCancelled:
    case OrderItemDetailsOrderLogReadDtoActionTypeEnum.QuoteAvailable:
    case OrderItemDetailsOrderLogReadDtoActionTypeEnum.QuoteValidated:
    case OrderItemDetailsOrderLogReadDtoActionTypeEnum.QuoteRefused:
      return (
        <OrderItemPageActivityLogQuote
          file={file}
          refusalReason={transitionData?.text}
          associatedOrderItemsLength={associatedOrderItemsLength}
          orderItemLog={orderItemLog}
        />
      );
    // Proofs
    case OrderItemDetailsOrderLogReadDtoActionTypeEnum.AddProof:
      if (!transitionData?.files?.length) {
        return <OrderItemPageActivityLogProof orderItemLog={orderItemLog} />;
      } else {
        return transitionData?.files?.map((file) => (
          <OrderItemPageActivityLogProof
            key={file.url ?? file.blobId}
            file={file}
            orderItemLog={orderItemLog}
          />
        ));
      }
    case OrderItemDetailsOrderLogReadDtoActionTypeEnum.ProofCancelledPreviouslyRefused:
    case OrderItemDetailsOrderLogReadDtoActionTypeEnum.ProofCancelledPreviouslyValidated:
    case OrderItemDetailsOrderLogReadDtoActionTypeEnum.ConfirmProof:
    case OrderItemDetailsOrderLogReadDtoActionTypeEnum.ProofCancelled:
    case OrderItemDetailsOrderLogReadDtoActionTypeEnum.RefusedProof:
      return (
        <OrderItemPageActivityLogProof
          file={transitionData?.files?.[0]}
          refusalReason={transitionData?.text}
          orderItemLog={orderItemLog}
        />
      );

    // PreMedia Breifing
    case OrderItemDetailsOrderLogReadDtoActionTypeEnum.PreMediaAvailable:
      return (
        <OrderItemPageActivityLogSimple
          content={t(
            "order.myOrdersItemDetails.activityFeed.action.preMediaAvailable"
          )}
        />
      );
    case OrderItemDetailsOrderLogReadDtoActionTypeEnum.PreMediaRefused:
      return (
        <OrderItemPageActivityLogSimple
          content={t(
            "order.myOrdersItemDetails.activityFeed.action.preMediaRefused"
          )}
        />
      );
    case OrderItemDetailsOrderLogReadDtoActionTypeEnum.PreMediaComplementaryInformationSubmitted:
      return (
        <OrderItemPageActivityLogSimple
          content={t(
            "order.myOrdersItemDetails.activityFeed.action.preMediaComplementaryInformationSubmitted"
          )}
        />
      );
    case OrderItemDetailsOrderLogReadDtoActionTypeEnum.PreMediaAccepted:
      return (
        <OrderItemPageActivityLogSimple
          content={t(
            "order.myOrdersItemDetails.activityFeed.action.preMediaAccepted"
          )}
        />
      );

    // PreMedia Proposition
    case OrderItemDetailsOrderLogReadDtoActionTypeEnum.PreMediaPropositionSubmitted:
      return (
        <OrderItemPageActivityLogSimple
          content={t(
            "order.myOrdersItemDetails.activityFeed.action.preMediaPropositionSubmitted"
          )}
        />
      );
    case OrderItemDetailsOrderLogReadDtoActionTypeEnum.PreMediaPropositionRefused:
    case OrderItemDetailsOrderLogReadDtoActionTypeEnum.PreMediaPropositionAccepted:
      return (
        <OrderItemPageActivityLogPreMediaProposition
          validated={
            orderItemLog.actionType ===
            OrderItemDetailsOrderLogReadDtoActionTypeEnum.PreMediaPropositionAccepted
          }
        />
      );

    // Rush Order
    case OrderItemDetailsOrderLogReadDtoActionTypeEnum.RushOrderAcceptedDate:
      return (
        <OrderItemPageActivityLogSimple
          content={t(
            "order.myOrdersItemDetails.activityFeed.action.rushOrderAcceptedDate"
          )}
        />
      );
    case OrderItemDetailsOrderLogReadDtoActionTypeEnum.RushOrderNewDateProposal:
      return (
        <OrderItemPageActivityLogSimple
          content={t(
            "order.myOrdersItemDetails.activityFeed.action.rushOrderNewDateProposal"
          )}
        />
      );

    // Delivery Notes
    case OrderItemDetailsOrderLogReadDtoActionTypeEnum.DeliveryNoteIntermediate:
      if (!transitionData?.files?.length) {
        return (
          <OrderItemPageActivityLogWithFiles
            intro={t(
              "order.myOrdersItemDetails.activityFeed.action.deliveryNoteIntermediate.intro"
            )}
            suffix={t(
              "order.myOrdersItemDetails.activityFeed.action.deliveryNoteIntermediate.suffix"
            )}
            file={transitionData?.files?.[0]}
          />
        );
      } else {
        return transitionData?.files?.map((file) => (
          <OrderItemPageActivityLogWithFiles
            key={file.url ?? file.blobId}
            intro={t(
              "order.myOrdersItemDetails.activityFeed.action.deliveryNoteIntermediate.intro"
            )}
            suffix={t(
              "order.myOrdersItemDetails.activityFeed.action.deliveryNoteIntermediate.suffix"
            )}
            file={file}
          />
        ));
      }

    case OrderItemDetailsOrderLogReadDtoActionTypeEnum.DeliveryNoteFinal:
      if (!transitionData?.files?.length) {
        return (
          <OrderItemPageActivityLogWithFiles
            intro={t(
              "order.myOrdersItemDetails.activityFeed.action.deliveryNoteFinal.intro"
            )}
            suffix={t(
              "order.myOrdersItemDetails.activityFeed.action.deliveryNoteFinal.suffix"
            )}
            file={transitionData?.files?.[0]}
          />
        );
      } else {
        return transitionData?.files?.map((file) => (
          <OrderItemPageActivityLogWithFiles
            key={file.url ?? file.blobId}
            intro={t(
              "order.myOrdersItemDetails.activityFeed.action.deliveryNoteFinal.intro"
            )}
            suffix={t(
              "order.myOrdersItemDetails.activityFeed.action.deliveryNoteFinal.suffix"
            )}
            file={file}
          />
        ));
      }

    // Delivered
    case OrderItemDetailsOrderLogReadDtoActionTypeEnum.Delivered:
      return (
        <OrderItemPageActivityLogSimpleWithIcon
          title={t("order.myOrdersItemDetails.activityFeed.action.delivered")}
          iconLabel="order.myOrdersItemDetails.activityFeed.action.infoIcon.delivered"
          iconState="success"
        />
      );

    // Client Reference
    case OrderItemDetailsOrderLogReadDtoActionTypeEnum.AddClientReference:
      return (
        <T
          keyName="order.myOrdersItemDetails.activityFeed.action.addClientReference"
          params={{
            reference: transitionData?.text,
            b: <KdsText size="subtitle-sm" />,
          }}
        />
      );
    case OrderItemDetailsOrderLogReadDtoActionTypeEnum.UpdateClientReference:
      return (
        <T
          keyName="order.myOrdersItemDetails.activityFeed.action.updateClientReference"
          params={{
            from: transitionData?.from,
            to: transitionData?.to,
            b: <KdsText size="subtitle-sm" />,
          }}
        />
      );

    case OrderItemDetailsOrderLogReadDtoActionTypeEnum.DeleteClientReference:
      return (
        <T
          keyName="order.myOrdersItemDetails.activityFeed.action.deleteClientReference"
          params={{
            clientReference: transitionData?.text,
            b: <KdsText size="subtitle-sm" />,
          }}
        />
      );

    // Estimated delivery date
    case OrderItemDetailsOrderLogReadDtoActionTypeEnum.AddEstimatedDeliveryDate:
      return (
        <T
          keyName="order.myOrdersItemDetails.activityFeed.action.addEstimatedDeliveryDate"
          params={{
            to: transitionData?.to
              ? dayjs(transitionData.to).format("L")
              : undefined,
            b: <KdsText size="subtitle-sm" />,
          }}
        />
      );

    case OrderItemDetailsOrderLogReadDtoActionTypeEnum.UpdateEstimatedDeliveryDate:
      return (
        <T
          keyName="order.myOrdersItemDetails.activityFeed.action.updateEstimatedDeliveryDate"
          params={{
            from: transitionData?.from
              ? dayjs(transitionData.from).format("L")
              : undefined,
            to: transitionData?.to
              ? dayjs(transitionData.to).format("L")
              : undefined,
            b: <KdsText size="subtitle-sm" />,
          }}
        />
      );

    case OrderItemDetailsOrderLogReadDtoActionTypeEnum.DeleteEstimatedDeliveryDate:
      return (
        <T
          keyName="order.myOrdersItemDetails.activityFeed.action.deleteEstimatedDeliveryDate"
          params={{
            to: transitionData?.from
              ? dayjs(transitionData.from).format("L")
              : undefined,
            b: <KdsText size="subtitle-sm" />,
          }}
        />
      );

    // Delivery Label
    case OrderItemDetailsOrderLogReadDtoActionTypeEnum.DeliveryLabelSubmitted:
      return (
        <OrderItemPageActivityLogWithFiles
          intro={t(
            "order.myOrdersItemDetails.activityFeed.action.deliveryLabelSubmitted.intro"
          )}
          suffix={t(
            "order.myOrdersItemDetails.activityFeed.action.deliveryLabelSubmitted.suffix"
          )}
          file={transitionData?.files?.[0]}
        />
      );
    case OrderItemDetailsOrderLogReadDtoActionTypeEnum.DeliveryLabelValidated:
      return (
        <OrderItemPageActivityLogWithFiles
          intro={t(
            "order.myOrdersItemDetails.activityFeed.action.deliveryLabelValidated.intro"
          )}
          suffix={t(
            "order.myOrdersItemDetails.activityFeed.action.deliveryLabelValidated.suffix"
          )}
          file={transitionData?.files?.[0]}
          iconLabel={OrderItemPageActivityLogMessageActionTypeEnum.VALIDATED}
        />
      );
    case OrderItemDetailsOrderLogReadDtoActionTypeEnum.DeliveryLabelRefused:
      return (
        <OrderItemPageActivityLogWithFiles
          intro={t(
            "order.myOrdersItemDetails.activityFeed.action.deliveryLabelRefused.intro"
          )}
          suffix={t(
            "order.myOrdersItemDetails.activityFeed.action.deliveryLabelRefused.suffix"
          )}
          file={transitionData?.files?.[0]}
          iconLabel={OrderItemPageActivityLogMessageActionTypeEnum.REFUSED}
          icon={RiCloseCircleFill}
          iconState="error"
        />
      );
  }

  return null;
};
